import {
  addLeaseCharge,
  addRecurringCharge,
  endLease,
  recordPayment,
  removeRecurringCharge,
  reverseLedgerEntry,
  setLeaseResidents,
  useGetLease,
  useGetLeaseLedger,
  useListNotifications,
  useListPayments,
  useListRecurringCharges,
  useListResidents,
  type LedgerRowOut,
  type PaymentIn,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field, type FormValues } from '../../components/FormFields';
import { StatRow, StatTile } from '../../components/Stats';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';
import { DepositSection } from './DepositSection';
import { METHOD_OPTIONS } from './labels';
import { MessageTable } from './MessageTable';
import { PaymentTable } from './PaymentTable';
import { TablePager, useTablePage } from '../../components/TablePager';

const PAYMENT_FIELDS: Field[] = [
  { name: 'amount', label: 'Amount', type: 'number', required: true },
  {
    name: 'kind',
    label: 'For',
    type: 'select',
    required: true,
    options: [
      { value: 'rent', label: 'Rent and charges' },
      { value: 'deposit', label: 'Security deposit' },
    ],
  },
  { name: 'method', label: 'Paid By', type: 'select', required: true, options: METHOD_OPTIONS },
  {
    name: 'reference',
    label: 'Check or Confirmation Number',
    helperText: 'Required for Zelle',
  },
  { name: 'received_on', label: 'Date Received', type: 'date', required: true },
  { name: 'memo', label: 'Note' },
];
const CHARGE_FIELDS: Field[] = [
  { name: 'description', label: 'What For', required: true },
  { name: 'amount', label: 'Amount', type: 'number', required: true },
  { name: 'charge_date', label: 'Date', type: 'date', required: true },
];
const RECURRING_FIELDS: Field[] = [
  { name: 'description', label: 'What For', required: true, helperText: 'Parking, pet fee…' },
  { name: 'amount', label: 'Monthly Amount', type: 'number', required: true },
];
const END_FIELDS: Field[] = [{ name: 'ended_on', label: 'Last Day', type: 'date', required: true }];
const REVERSE_FIELDS: Field[] = [{ name: 'reason', label: 'Reason', required: true }];

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Stack>
  );
}

type Dialog =
  | { kind: 'payment' | 'charge' | 'recurring' | 'end' | 'resident'; today: string }
  | { kind: 'reverse'; entry: LedgerRowOut };

export function LeaseDetailPage() {
  const leaseId = Number(useParams().leaseId);
  const { data, isPending } = useGetLease(leaseId);
  const lease = okData(data);
  const ledger = okData(useGetLeaseLedger(leaseId).data);
  const messagePaging = useTablePage();
  const messageQuery = useListNotifications({ lease_id: leaseId, ...messagePaging.params });
  const messages = okItems(messageQuery.data);
  const paymentPaging = useTablePage();
  const paymentQuery = useListPayments({ lease_id: leaseId, ...paymentPaging.params });
  const payments = okItems(paymentQuery.data);
  const recurring = okData(useListRecurringCharges(leaseId).data)?.items ?? [];
  const residents = okItems(useListResidents({ page_size: 200 }).data);
  const canManage = useCan('leases.manage');
  const canTakeMoney = useCan('payments.manage');
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const open = (kind: 'payment' | 'charge' | 'recurring' | 'end' | 'resident') =>
    setDialog({ kind, today: todayIso() });
  const close = () => setDialog(null);

  if (isPending) return <CircularProgress aria-label="Loading" />;
  if (!lease) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Alert severity="error">We couldn't find that lease.</Alert>
        <Button component={Link} to="/staff/leases">
          Back to leases
        </Button>
      </Stack>
    );
  }

  const isActive = lease.status === 'active';
  const balance = Number(lease.balance);
  const onLease = new Set(lease.residents.map((r) => r.resident_id));
  const addable = residents.filter((r) => !onLease.has(r.id));
  const removeResident = async (residentId: number) => {
    await setLeaseResidents(leaseId, {
      resident_ids: lease.residents.map((r) => r.resident_id).filter((id) => id !== residentId),
    });
    await queryClient.invalidateQueries();
  };
  const removeCharge = async (chargeId: number) => {
    await removeRecurringCharge(leaseId, chargeId);
    await queryClient.invalidateQueries();
  };

  const ledgerColumns: DataColumn<LedgerRowOut>[] = [
    { label: 'Date', render: (r) => formatDate(r.entry_date) },
    {
      label: 'Description',
      render: (r) => (
        <>
          {r.memo} {r.is_reversed && <Chip size="small" label="Reversed" />}
        </>
      ),
    },
    {
      label: 'Charge',
      align: 'right',
      render: (r) => (Number(r.charge) ? formatMoney(r.charge) : ''),
    },
    {
      label: 'Payment',
      align: 'right',
      render: (r) => (Number(r.payment) ? formatMoney(r.payment) : ''),
    },
    { label: 'Balance', align: 'right', render: (r) => formatMoney(r.balance) },
    ...(canTakeMoney
      ? [
          {
            label: 'Actions',
            align: 'right' as const,
            render: (r: LedgerRowOut) =>
              r.can_reverse && (
                <Button size="small" onClick={() => setDialog({ kind: 'reverse', entry: r })}>
                  Reverse
                </Button>
              ),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={4}>
      <div>
        <Button component={Link} to="/staff/leases" size="small" sx={{ ml: -1 }}>
          ‹ Leases
        </Button>
        <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            <MuiLink component={Link} to={`/staff/properties/${lease.property_id}`} color="inherit">
              {lease.property_name}
            </MuiLink>{' '}
            · {lease.unit_number}
          </Typography>
          {canTakeMoney && (
            <>
              <Button variant="contained" onClick={() => open('payment')}>
                Record payment
              </Button>
              <Button variant="outlined" onClick={() => open('charge')}>
                Add charge
              </Button>
            </>
          )}
          {canManage && isActive && (
            <Button color="error" onClick={() => open('end')}>
              End lease
            </Button>
          )}
        </Stack>
        <Typography color="text.secondary">
          {isActive ? 'Active' : 'Ended'} · {formatDate(lease.start_date)} to{' '}
          {lease.end_date ? formatDate(lease.end_date) : 'month to month'} · Due on day{' '}
          {lease.due_day}
        </Typography>
        {lease.notes && <Typography sx={{ mt: 1 }}>{lease.notes}</Typography>}
      </div>

      <StatRow>
        <StatTile
          label={balance < 0 ? 'Credit' : 'Balance owed'}
          value={formatMoney(Math.abs(balance))}
        />
        <StatTile label="Monthly Rent" value={formatMoney(lease.rent_amount)} />
        <StatTile
          label="Deposit Held"
          value={ledger && formatMoney(ledger.deposit_held)}
          hint={`Of ${formatMoney(lease.deposit_amount)} on the lease`}
        />
      </StatRow>

      <Section title="Ledger">
        {ledger?.rows.length === 0 ? (
          <Typography color="text.secondary">
            No charges or payments yet. Rent posts automatically each month.
          </Typography>
        ) : (
          <DataTable columns={ledgerColumns} rows={ledger?.rows ?? []} rowKey={(r) => r.entry_id} />
        )}
      </Section>

      <Section title="Payments">
        {payments.length === 0 ? (
          <Typography color="text.secondary">No payments yet.</Typography>
        ) : (
          <>
            <PaymentTable payments={payments} />
            <TablePager paging={paymentPaging} total={okData(paymentQuery.data)?.total ?? 0} />
          </>
        )}
      </Section>

      <Section title="Messages Sent">
        {messages.length === 0 ? (
          <Typography color="text.secondary">No messages yet.</Typography>
        ) : (
          <>
            <MessageTable messages={messages} />
            <TablePager paging={messagePaging} total={okData(messageQuery.data)?.total ?? 0} />
          </>
        )}
      </Section>

      <DepositSection
        leaseId={leaseId}
        isActive={isActive}
        held={Number(ledger?.deposit_held ?? 0)}
        balance={balance}
      />

      <Section
        title="Residents"
        action={
          canManage &&
          isActive && (
            <Button variant="outlined" size="small" onClick={() => open('resident')}>
              Add co-resident
            </Button>
          )
        }
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
            {lease.residents.map((r) => (
              <Chip
                key={r.resident_id}
                label={`${r.name}${r.is_primary ? ' (primary)' : ''}`}
                onDelete={
                  canManage && isActive && lease.residents.length > 1
                    ? () => void removeResident(r.resident_id)
                    : undefined
                }
              />
            ))}
          </Stack>
        </Paper>
      </Section>

      <Section
        title="Monthly Charges"
        action={
          canManage && (
            <Button variant="outlined" size="small" onClick={() => open('recurring')}>
              Add monthly charge
            </Button>
          )
        }
      >
        {recurring.length === 0 ? (
          <Typography color="text.secondary">Only rent is charged each month.</Typography>
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              {recurring.map((charge) => (
                <Stack key={charge.id} direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ flexGrow: 1 }}>{charge.description}</Typography>
                  <Typography>{formatMoney(charge.amount)}</Typography>
                  {canManage && (
                    <IconButton
                      size="small"
                      aria-label={`Remove ${charge.description}`}
                      onClick={() => void removeCharge(charge.id)}
                    >
                      ×
                    </IconButton>
                  )}
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}
      </Section>

      <RecordExtras entityType="lease" entityId={leaseId} canManage={canManage} />

      {dialog?.kind === 'payment' && (
        <FormDialog
          title="Record Payment"
          fields={PAYMENT_FIELDS}
          initial={{
            amount: balance > 0 ? balance.toFixed(2) : '',
            kind: 'rent',
            method: 'check',
            reference: '',
            received_on: dialog.today,
            memo: '',
          }}
          submitLabel="Record payment"
          onSubmit={(values: FormValues) =>
            recordPayment(leaseId, {
              amount: required(values, 'amount'),
              kind: required(values, 'kind') as PaymentIn['kind'],
              method: required(values, 'method') as PaymentIn['method'],
              reference: optional(values, 'reference'),
              received_on: required(values, 'received_on'),
              memo: optional(values, 'memo'),
            })
          }
          onClose={close}
        />
      )}
      {dialog?.kind === 'charge' && (
        <FormDialog
          title="Add a One-Time Charge"
          fields={CHARGE_FIELDS}
          initial={{ description: '', amount: '', charge_date: dialog.today }}
          submitLabel="Add charge"
          onSubmit={(values) =>
            addLeaseCharge(leaseId, {
              description: required(values, 'description'),
              amount: required(values, 'amount'),
              charge_date: required(values, 'charge_date'),
            })
          }
          onClose={close}
        />
      )}
      {dialog?.kind === 'recurring' && (
        <FormDialog
          title="Add a Monthly Charge"
          intro="Charged with rent each month from the next posting."
          fields={RECURRING_FIELDS}
          initial={{ description: '', amount: '' }}
          onSubmit={(values) =>
            addRecurringCharge(leaseId, {
              description: required(values, 'description'),
              amount: required(values, 'amount'),
            })
          }
          onClose={close}
        />
      )}
      {dialog?.kind === 'resident' && (
        <FormDialog
          title="Add a Co-Resident"
          fields={[
            {
              name: 'resident_id',
              label: 'Resident',
              type: 'select',
              required: true,
              options: addable.map((r) => ({
                value: String(r.id),
                label: `${r.last_name}, ${r.first_name}`,
              })),
            },
          ]}
          initial={{ resident_id: '' }}
          submitLabel="Add"
          onSubmit={(values) =>
            setLeaseResidents(leaseId, {
              resident_ids: [
                ...lease.residents.map((r) => r.resident_id),
                Number(required(values, 'resident_id')),
              ],
            })
          }
          onClose={close}
        />
      )}
      {dialog?.kind === 'end' && (
        <FormDialog
          title="End This Lease"
          intro="The unit becomes vacant and its residents leave it. Rent stops, and anything owed stays on this ledger."
          fields={END_FIELDS}
          initial={{ ended_on: lease.end_date ?? dialog.today }}
          submitLabel="End lease"
          onSubmit={(values) => endLease(leaseId, { ended_on: required(values, 'ended_on') })}
          onClose={close}
        />
      )}
      {dialog?.kind === 'reverse' && (
        <FormDialog
          title={`Reverse ${formatMoney(dialog.entry.charge)} Charge`}
          intro="The original stays on the ledger, with a new entry that cancels it. Rent for that month won't post again."
          fields={REVERSE_FIELDS}
          initial={{ reason: '' }}
          submitLabel="Reverse charge"
          onSubmit={(values) =>
            reverseLedgerEntry(dialog.entry.entry_id, { reason: required(values, 'reason') })
          }
          onClose={close}
        />
      )}
    </Stack>
  );
}
